/**
 * Wall-clock cap for cold-visit SPA loading gates.
 * Per-request timeouts (AuthGuard /me, page apiFetch, refresh) can stack or be
 * skipped (raw fetch). This deadline guarantees a spinner cannot last forever.
 */
export const COLD_LOAD_SPINNER_MAX_MS = 5_000;

/**
 * Resolve web auth UI before the first /api/auth/me round-trip.
 * Guest → sign-in immediately. Cached token+user → render the route and
 * revalidate in the background. Token without a cached user → pending spinner.
 *
 * @param {{
 *   getToken?: () => string | null,
 *   getCachedUser?: () => object | null,
 * }} [deps]
 * @returns {{ kind: 'guest' } | { kind: 'cached', user: object } | { kind: 'pending' }}
 */
export function readWebAuthBootState({
  getToken = () =>
    typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null,
  getCachedUser,
} = {}) {
  try {
    const token = getToken();
    if (!token) return { kind: 'guest' };
    const user = getCachedUser ? getCachedUser() : null;
    if (user && typeof user === 'object' && user.id) return { kind: 'cached', user };
    return { kind: 'pending' };
  } catch {
    return { kind: 'pending' };
  }
}

/**
 * Fire `onDeadline` once if the caller does not cancel first.
 * @param {() => void} onDeadline
 * @param {number} [timeoutMs]
 * @returns {() => void} cancel
 */
export function armColdLoadDeadline(
  onDeadline,
  timeoutMs = COLD_LOAD_SPINNER_MAX_MS,
) {
  let settled = false;
  const id = setTimeout(() => {
    if (settled) return;
    settled = true;
    onDeadline();
  }, timeoutMs);
  return () => {
    settled = true;
    clearTimeout(id);
  };
}
